import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  CreditCard,
  RefreshCw,
  AlertTriangle,
  FlaskConical,
} from 'lucide-react';
import {
  createMobbexSubscription,
  isMobbexMock,
  type MobbexSubscriptionResult,
} from '../lib/mobbexSubscription';
import { openMobbexCheckout } from '../lib/mobbexEmbed';

// Monto de la suscripción de prueba (ARS, recurrente mensual).
const TOTAL_ARS = 10000;

type Phase = 'idle' | 'creating' | 'opening' | 'success' | 'rejected' | 'error';

const formatArs = (n: number) =>
  `$${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

export const MobbexTest: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [raw, setRaw] = useState<unknown>(null);

  const busy = phase === 'creating' || phase === 'opening';

  const reset = () => {
    setPhase('idle');
    setMessage(null);
    setRaw(null);
  };

  const handleSubscribe = async () => {
    setPhase('creating');
    setMessage(null);
    setRaw(null);

    const result: MobbexSubscriptionResult = await createMobbexSubscription({
      total: TOTAL_ARS,
      currency: 'ARS',
      interval: 'monthly',
      reference: `test-mobbex-${TOTAL_ARS}`,
    });

    if (result.status === 'error') {
      setMessage(result.message ?? 'Ocurrió un error.');
      setRaw(result.raw);
      setPhase('error');
      return;
    }
    if (result.status === 'rejected') {
      setMessage(result.message ?? 'La suscripción fue rechazada.');
      setRaw(result.raw);
      setPhase('rejected');
      return;
    }

    // authorized
    if (result.mock || !result.subscriptionId) {
      // Modo mock: no hay checkout real que abrir, simulamos el éxito.
      setRaw(result.raw);
      setPhase('success');
      return;
    }

    // Modo real: abrir el componente Embed de Mobbex con los ids del backend.
    setPhase('opening');
    try {
      await openMobbexCheckout(
        {
          subscriptionId: result.subscriptionId,
          subscriberId: result.subscriberId,
        },
        {
          onResult: (data) => {
            setRaw(data);
            setPhase('success');
          },
          onError: (err) => {
            setMessage('Error en el checkout de Mobbex.');
            setRaw(err);
            setPhase('error');
          },
          onClose: (cancelled) => {
            // Si cerró sin resultado, volvemos al inicio.
            setPhase((p) => (p === 'opening' && cancelled ? 'idle' : p));
          },
        },
      );
    } catch (err) {
      setMessage((err as Error)?.message ?? 'No se pudo abrir Mobbex.');
      setRaw(err);
      setPhase('error');
    }
  };

  return (
    <div className="min-h-full w-full flex items-start justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-brand-black">
            Suscripción Mobbex
          </h1>
          <p className="mt-1 text-sm text-brand-black/60">
            Página de prueba de la integración con Mobbex (Argentina).
          </p>
        </div>

        {/* Card del plan */}
        <div className="rounded-2xl border border-brand-black/10 bg-white p-6 shadow-sm">
          {isMobbexMock && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-amber-700">
              <FlaskConical className="h-4 w-4 shrink-0" />
              <span className="text-xs font-medium">
                Modo MOCK — sin backend ni cobro real
              </span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-black/5">
              <CreditCard className="h-5 w-5 text-brand-black" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-black">Plan de prueba</p>
              <p className="text-xs text-brand-black/50">Cobro recurrente mensual</p>
            </div>
          </div>

          <div className="mt-5 flex items-baseline gap-1.5">
            <span className="text-4xl font-bold text-brand-black">
              {formatArs(TOTAL_ARS)}
            </span>
            <span className="text-sm text-brand-black/50">ARS / mes</span>
          </div>

          {/* Resultado / acción */}
          <div className="mt-6">
            {phase === 'success' && (
              <ResultBox
                tone="success"
                icon={<CheckCircle2 className="h-5 w-5" />}
                title="¡Suscripción creada!"
                detail={
                  isMobbexMock
                    ? 'Resultado simulado (mock). Cuando conectes el backend, acá se abriría el checkout real de Mobbex.'
                    : 'El cobro fue autorizado por Mobbex.'
                }
              />
            )}
            {phase === 'rejected' && (
              <ResultBox
                tone="error"
                icon={<XCircle className="h-5 w-5" />}
                title="Suscripción rechazada"
                detail={message ?? undefined}
              />
            )}
            {phase === 'error' && (
              <ResultBox
                tone="error"
                icon={<AlertTriangle className="h-5 w-5" />}
                title="Hubo un error"
                detail={message ?? undefined}
              />
            )}

            {phase === 'success' || phase === 'rejected' || phase === 'error' ? (
              <button
                onClick={reset}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-brand-black/15 bg-white py-3 px-6 text-sm font-medium text-brand-black transition hover:bg-brand-black/[0.03]"
              >
                <RefreshCw className="h-4 w-4" />
                Probar de nuevo
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-black py-3 px-6 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {phase === 'opening' ? 'Abriendo Mobbex…' : 'Procesando…'}
                  </>
                ) : (
                  <>Suscribirse con Mobbex</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Debug: respuesta cruda */}
        {raw != null && (
          <details className="mt-4 rounded-xl border border-brand-black/10 bg-brand-black/[0.02] p-3">
            <summary className="cursor-pointer text-xs font-medium text-brand-black/60">
              Ver respuesta (debug)
            </summary>
            <pre className="mt-2 overflow-x-auto text-[11px] leading-relaxed text-brand-black/70">
              {JSON.stringify(raw, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

function ResultBox({
  tone,
  icon,
  title,
  detail,
}: {
  tone: 'success' | 'error';
  icon: React.ReactNode;
  title: string;
  detail?: string;
}) {
  const styles =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-red-50 text-red-700';
  return (
    <div className={`flex items-start gap-3 rounded-xl px-4 py-3 ${styles}`}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {detail && <p className="mt-0.5 text-xs opacity-80">{detail}</p>}
      </div>
    </div>
  );
}

export default MobbexTest;
